// App.js
import React, { useState, useEffect, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate } from "react-router-dom";
import { auth, db } from "./firebase.js";
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from "firebase/auth";
import { collection, getDocs, setDoc, doc, deleteDoc } from "firebase/firestore";
import backgroundImage from './assets/nmr_main.jpg';
import OwnersPage from "./OwnersPage.js";
import InvoicesPage from "./InvoicesPage.js";
import ContractorsPage from "./ContractorsPage.js";
import CalendarPage from "./CalendarPage.js";
import BoardMembersPage from "./BoardMembersPage.js";
import MeetingMinutesPage from "./MeetingMinutesPage.js";
import CamerasPage from "./CamerasPage.js";
import HOADocsPage from "./HOADocsPage.js";
import AdminInvoicesPage from "./AdminInvoicesPage.js";
import RentalsPage from "./RentalsPage.js";
import CommunityPicsPage from "./CommunityPicsPage.js";
import MediaDetailPage from "./MediaDetailPage.js";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from "chart.js";
import useIsMobile from './useIsMobile.js';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

console.log("React Router imports:", { Router, Routes, Route, Link, useNavigate, Navigate });
console.log("Firebase imports:", { auth, db, signInWithEmailAndPassword, signOut, collection, getDocs });

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught in ErrorBoundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Error: {this.state.error.message}</div>;
    }
    return this.props.children;
  }
}

function Header({ onLoginClick, isPublic, user, isAdmin, onLogout, navigate, isMobile }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header className="header">
      <div className="header-content">
        {!isPublic && (
          <button className="hamburger-button" onClick={toggleMenu}>
            <span className="hamburger-icon"></span>
          </button>
        )}
        <div className="header-title-container">
          <h1 className="header-title">Nantahala Mountain Resort HOA</h1>
          {user && (
            <p className={`header-user ${isAdmin ? "text-red-500" : "text-gray-300"}`}>
              Logged in as: {user.email} {isAdmin && "(Admin)"}
            </p>
          )}
        </div>
        {isPublic ? (
          <div className="nav-buttons">
            <Link to="/rentals" className="header-login-button">
              Rentals
            </Link>
            <Link to="/" className="header-login-button">
              Main Page
            </Link>
            <button
              onClick={onLoginClick}
              className="header-login-button"
            >
              Owner's Login
            </button>
          </div>
        ) : (
          <>
            <div className={`header-nav ${isMenuOpen ? 'header-nav-open' : ''}`}>
              <button
                onClick={() => { navigate("/dashboard"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                Home
              </button>
              <button
                onClick={() => { navigate("/cameras"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                Cameras
              </button>
              <button
                onClick={() => { navigate("/community-pics"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                Community Pics
              </button>
              <button
                onClick={() => { navigate("/owners"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                Owner Contacts
              </button>
              <button
                onClick={() => { navigate("/contractors"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                Contractors
              </button>
              <button
                onClick={() => { navigate("/calendar"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                Calendar
              </button>
              <button
                onClick={() => { navigate("/board-members"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                Board Members
              </button>
              <button
                onClick={() => { navigate("/meeting-minutes"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                Meeting Minutes
              </button>
              <button
                onClick={() => { navigate("/hoa-docs"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                HOA Docs
              </button>
              <button
                onClick={() => { navigate("/invoices"); setIsMenuOpen(false); }}
                className="header-nav-button"
              >
                View Invoices
              </button>
              {isAdmin && (
                <button
                  onClick={() => { navigate("/rentals"); setIsMenuOpen(false); }}
                  className="header-nav-button"
                >
                  Rentals
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => { navigate("/admin-invoices"); setIsMenuOpen(false); }}
                  className="header-nav-button"
                >
                  Admin Invoices
                </button>
              )}
              {isMobile && (
                <button
                  onClick={() => { onLogout(); setIsMenuOpen(false); }}
                  className="header-logout-button in-menu"
                >
                  Logout
                </button>
              )}
            </div>
            {!isMobile && (
              <button
                onClick={onLogout}
                className="header-logout-button"
              >
                Logout
              </button>
            )}
          </>
        )}
      </div>
    </header>
  );
}

function LoginWindow({ onClose, isMobile }) {
  console.log("Rendering LoginWindow component");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleLogin = async () => {
    try {
      console.log("Attempting login with email:", email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      const token = await user.getIdToken();
      console.log("Firebase ID Token:", token);
      setError(null);
      onClose();
      navigate("/dashboard");
    } catch (error) {
      console.error("Login failed:", error);
      setError("Login failed: " + error.message);
    }
  };

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
        <h2 className="modal-title">Owner's Login</h2>
        {error && <p className="modal-error">{error}</p>}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="modal-input"
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="modal-input"
        />
        <button
          onClick={handleLogin}
          className="modal-button modal-button-primary"
        >
          Login
        </button>
        <button
          onClick={onClose}
          className="modal-button modal-button-secondary"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function EmergencyMessageDialog({ isOpen, onClose, currentMessage, onSave, onDelete, isMobile }) {
  const [message, setMessage] = useState(currentMessage || "");

  useEffect(() => {
    setMessage(currentMessage || "");
  }, [currentMessage, isOpen]);

  const handleSave = () => {
    onSave(message);
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className={`modal-content ${isMobile ? 'modal-content-mobile' : ''}`}>
        <h2 className="modal-title">Manage Emergency Message</h2>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter emergency message"
          className="modal-textarea"
        />
        <div className="modal-button-group">
          <button
            onClick={handleSave}
            className="modal-button modal-button-primary"
          >
            Save
          </button>
          <button
            onClick={handleDelete}
            className="modal-button modal-button-danger"
          >
            Delete
          </button>
          <button
            onClick={onClose}
            className="modal-button modal-button-secondary"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function MainPage({ onLoginClick, isPublic, user, isAdmin, weatherData, historicalWeatherData, events, eventsError, isMobile }) {
  console.log("Rendering MainPage component");
  const [emergencyMessage, setEmergencyMessage] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchEmergencyMessage = async () => {
      if (!user) {
        console.log("Skipping fetchEmergencyMessage: User not authenticated");
        return;
      }
      try {
        const snapshot = await getDocs(collection(db, "emergency_alerts"));
        if (!snapshot.empty) {
          const alertDoc = snapshot.docs[0];
          setEmergencyMessage(alertDoc.data().message || "");
        } else {
          setEmergencyMessage("");
        }
      } catch (error) {
        console.error("Error fetching emergency message:", error);
      }
    };
    fetchEmergencyMessage();
  }, [user]);

  const handleEmergencyMessage = async (message) => {
    if (!isAdmin) return;

    try {
      await setDoc(doc(db, "emergency_alerts", "active_alert"), {
        message: message,
        createdAt: new Date(),
        createdBy: user.uid,
      });
      setEmergencyMessage(message);
    } catch (error) {
      console.error("Error saving emergency message:", error);
    }
  };

  const handleDeleteEmergencyMessage = async () => {
    if (!isAdmin) return;

    try {
      await deleteDoc(doc(db, "emergency_alerts", "active_alert"));
      setEmergencyMessage("");
    } catch (error) {
      console.error("Error deleting emergency message:", error);
    }
  };

  const chartData = historicalWeatherData ? {
    labels: historicalWeatherData.map(data => new Date(data.obsTimeLocal).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })),
    datasets: [
      {
        label: 'Temperature (°F)',
        data: historicalWeatherData.map(data => data.imperial.tempAvg),
        fill: false,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        tension: 0.1,
      },
      {
        label: 'Wind Speed (mph)',
        data: historicalWeatherData.map(data => data.imperial.windspeedAvg),
        fill: false,
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        tension: 0.1,
      },
      {
        label: 'Pressure (inHg)',
        data: historicalWeatherData.map(data => data.imperial.pressureMax),
        fill: false,
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        tension: 0.1,
      },
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: '24-Hour Weather Trends',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Time',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Value',
        },
        beginAtZero: false,
      },
    },
  };

  // Filter events to only include those within the next 30 days
  const currentDate = new Date();
  const thirtyDaysFromNow = new Date(currentDate.getTime() + 30 * 24 * 60 * 60 * 1000);
  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.start);
    return eventDate >= currentDate && eventDate <= thirtyDaysFromNow;
  });

  return (
    <div className="main-page-container">
      <main className="main-page-content">
        <div className="content-wrapper">
          {!isPublic && (
            <>
              <div className={`content-section ${isMobile ? 'content-section-mobile' : ''}`}>
                <h2 className="content-title">Upcoming Events</h2>
                {eventsError ? (
                  <p className="text-red-500">{eventsError}</p>
                ) : upcomingEvents.length > 0 ? (
                  <ul>
                    {upcomingEvents.map((event) => (
                      <li key={event.id}>
                        {event.title} - {event.date}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No upcoming events within the next 30 days</p>
                )}
              </div>

              {emergencyMessage && (
                <div className={`emergency-message ${isMobile ? 'emergency-message-mobile' : ''}`}>
                  <h2 className="emergency-title">Emergency Alert</h2>
                  <p>{emergencyMessage}</p>
                </div>
              )}

              {isAdmin && (
                <div className="mb-4">
                  <button
                    onClick={() => setIsDialogOpen(true)}
                    className="emergency-button"
                  >
                    Manage Emergency Message
                  </button>
                </div>
              )}

              <EmergencyMessageDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                currentMessage={emergencyMessage}
                onSave={handleEmergencyMessage}
                onDelete={handleDeleteEmergencyMessage}
                isMobile={isMobile}
              />
            </>
          )}

          <div className="flex-grow"></div>

          <div className={`weather-section ${isMobile ? 'weather-section-mobile' : ''}`}>
            <h2 className="weather-title">
              Current Conditions at Nantahala Mountain Resort at: {(weatherData && weatherData.rawData?.obsTimeLocal) || "N/A"}
            </h2>
            {weatherData ? (
              <div className={`weather-grid ${isMobile ? 'weather-grid-mobile' : ''}`}>
                <div>
                  <p><strong>Temperature:</strong> {weatherData.temp}</p>
                  <p><strong>Humidity:</strong> {weatherData.humidity}</p>
                  <p><strong>Wind:</strong> {weatherData.wind}</p>
                  {weatherData.rawData && (
                    <>
                      <p><strong>Heat Index:</strong> {weatherData.rawData.imperial.heatIndex}</p>
                      <p><strong>Dew Point:</strong> {weatherData.rawData.imperial.dewpt}°F</p>
                    </>
                  )}
                </div>
                <div>
                  {weatherData.rawData && (
                    <>
                      <p><strong>Wind Chill:</strong> {weatherData.rawData.imperial.windChill}°F</p>
                      <p><strong>Wind Gust:</strong> {weatherData.rawData.imperial.windGust} mph</p>
                      <p><strong>Pressure:</strong> {weatherData.rawData.imperial.pressure} inHg</p>
                    </>
                  )}
                </div>
                <div>
                  {weatherData.rawData && (
                    <>
                      <p><strong>Precip Rate:</strong> {weatherData.rawData.imperial.precipRate} in/hr</p>
                      <p><strong>Precip Total:</strong> {weatherData.rawData.imperial.precipTotal} in</p>
                      <p><strong>Elevation:</strong> {weatherData.rawData.imperial.elev} ft</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-center">Weather data unavailable</p>
            )}
            {historicalWeatherData && historicalWeatherData.length > 0 ? (
              <div className={`weather-chart ${isMobile ? 'weather-chart-mobile' : ''}`}>
                <Line data={chartData} options={chartOptions} />
              </div>
            ) : (
              <p className="mt-4 text-center">No historical weather data available for the graph</p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function ProtectedRoute({ children, isMobile }) {
  console.log("Rendering ProtectedRoute component");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log("Setting up auth state listener in ProtectedRoute");
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        console.log("Auth state changed. Current user:", user ? user.uid : "No user");
        if (!user) {
          navigate("/");
          setAuthLoading(false);
        } else {
          console.log("Forcing token refresh for user:", user.uid);
          const token = await user.getIdToken(true);
          const idTokenResult = await user.getIdTokenResult();
          console.log("Refreshed token:", token);
          console.log("User authenticated:", user.uid);
          console.log("User custom claims:", idTokenResult.claims);
          const adminStatus = idTokenResult.claims.isAdmin === true;
          setUser(user);
          setIsAdmin(adminStatus);
          console.log("ProtectedRoute: isAdmin set to:", adminStatus);
          await new Promise(resolve => setTimeout(resolve, 2000));
          setAuthLoading(false);
        }
      } catch (err) {
        console.error("Error in auth state change handler:", err);
        setError(err.message);
        setAuthLoading(false);
      }
    }, (error) => {
      console.error("Auth state change error:", error);
      setError(error.message);
      setAuthLoading(false);
    });
    return () => {
      console.log("Cleaning up auth state listener in ProtectedRoute");
      unsubscribe();
    };
  }, [navigate]);

  if (authLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!user) return null;

  console.log("Rendering children in ProtectedRoute with isAdmin:", isAdmin);
  console.log("Children type:", typeof children);
  console.log("Children:", children);

  const childrenArray = React.Children.toArray(children);
  console.log("Children array:", childrenArray);

  if (childrenArray.length === 0) {
    console.error("Children array is empty in ProtectedRoute");
    return <div>Error: No children to render</div>;
  }

  try {
    return (
      <>
        {childrenArray.map((child, index) => {
          console.log(`Rendering child ${index}:`, child.type?.name || child.type, "with isAdmin:", isAdmin);
          return React.cloneElement(child, { user, isAdmin, authLoading, isMobile, key: index });
        })}
      </>
    );
  } catch (err) {
    console.error("Error rendering children in ProtectedRoute:", err);
    return <div>Error rendering children: {err.message}</div>;
  }
}

function HeaderWrapper({ onLoginClick, isPublic, user, isAdmin, onLogout, isMobile }) {
  const navigate = useNavigate();
  return (
    <Header
      onLoginClick={onLoginClick}
      isPublic={isPublic}
      user={user}
      isAdmin={isAdmin}
      onLogout={onLogout}
      navigate={navigate}
      isMobile={isMobile}
    />
  );
}

function App() {
  console.log("Rendering App component");
  const [showLogin, setShowLogin] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [historicalWeatherData, setHistoricalWeatherData] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsError, setEventsError] = useState(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await user.getIdToken(true);
        const idTokenResult = await user.getIdTokenResult();
        console.log("App: User authenticated:", user.uid);
        console.log("App: User custom claims:", idTokenResult.claims);
        const adminStatus = idTokenResult.claims.isAdmin === true;
        setUser(user);
        setIsAdmin(adminStatus);
        console.log("App: isAdmin set to:", adminStatus);
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    console.log("Attempting to fetch current weather data from Weather Underground...");
    fetch(
      "https://api.weather.com/v2/pws/observations/current?stationId=KNCTOPTO33&format=json&units=e&apiKey=01ed8c3908e14ceead8c3908e14cee78"
    )
      .then((res) => {
        console.log("Current Weather API response status:", res.status);
        if (!res.ok) throw new Error(`Current Weather API request failed with status ${res.status}`);
        return res.json();
      })
      .then((data) => {
        console.log("Current Weather API response data:", data);
        if (data.observations && data.observations.length > 0) {
          const observation = data.observations[0];
          setWeatherData({
            temp: `${observation.imperial.temp}°F`,
            humidity: `${observation.humidity}%`,
            wind: `${observation.imperial.windSpeed} mph`,
            rawData: observation
          });
        } else {
          console.log("No observations in current weather response, setting default weather data");
          setWeatherData({ temp: "N/A", humidity: "N/A", wind: "N/A" });
        }
      })
      .catch((err) => {
        console.error("Current Weather API error:", err);
        setWeatherData({ temp: "Error", humidity: "Error", wind: "Error" });
      });

    console.log("Attempting to fetch historical weather data from Weather Underground...");
    fetch(
      "https://api.weather.com/v2/pws/observations/hourly/7day?stationId=KNCTOPTO33&format=json&units=e&apiKey=01ed8c3908e14ceead8c3908e14cee78"
    )
      .then((res) => {
        console.log("Historical Weather API response status:", res.status);
        if (!res.ok) throw new Error(`Historical Weather API request failed with status ${res.status}`);
        return res.json();
      })
      .then((data) => {
        console.log("Historical Weather API response data:", data);
        if (data.observations && data.observations.length > 0) {
          const sortedData = data.observations.sort((a, b) => new Date(b.obsTimeUtc) - new Date(a.obsTimeUtc));
          console.log("Sorted historical data (newest first):", sortedData);

          const latestTime = new Date(sortedData[0].obsTimeUtc);
          const twentyFourHoursAgo = new Date(latestTime.getTime() - 24 * 60 * 60 * 1000);
          const filteredData = sortedData
            .filter(obs => {
              const obsTime = new Date(obs.obsTimeUtc);
              const isWithin24Hours = obsTime >= twentyFourHoursAgo;
              console.log(`Observation time: ${obsTime}, Within 24 hours from latest (${latestTime}): ${isWithin24Hours}`);
              return isWithin24Hours;
            })
            .sort((a, b) => new Date(a.obsTimeUtc) - new Date(b.obsTimeUtc));
          
          console.log("Filtered 24-hour historical data:", filteredData);
          setHistoricalWeatherData(filteredData);
        } else {
          console.log("No observations in historical weather response");
          setHistoricalWeatherData([]);
        }
      })
      .catch((err) => {
        console.error("Historical Weather API error:", err);
        setHistoricalWeatherData([]);
      });

    const fetchEvents = async () => {
      if (!user) {
        console.log("Skipping fetchEvents: User not authenticated yet.");
        return;
      }
      try {
        console.log("Fetching events from Firestore...");
        const snapshot = await getDocs(collection(db, "events"));
        console.log("Events snapshot size:", snapshot.size);
        const eventList = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.title,
            date: data.date,
            start: new Date(data.date),
            end: new Date(data.date),
            allDay: true
          };
        });
        console.log("Events list:", eventList);
        setEvents(eventList);
        setEventsError(null);
      } catch (err) {
        console.error("Error fetching events:", err);
        setEventsError("Failed to load events: " + err.message);
        setEvents([]);
      }
    };

    if (user) {
      fetchEvents();
    }
  }, [user]);

  const handleLoginClick = () => {
    setShowLogin(true);
  };

  const handleLoginClose = () => {
    setShowLogin(false);
  };

  const handleLogout = () => {
    signOut(auth);
  };

  return (
    <ErrorBoundary>
      <Suspense fallback={<div>Loading...</div>}>
        <Router>
          <div className="app-container">
            <style>
              {`
                .app-container {
                  display: flex;
                  flex-direction: column;
                  min-height: 100vh;
                  background-image: url(${backgroundImage});
                  background-size: cover;
                  background-position: center;
                }
                .header {
                  background: #1a3c34;
                  color: white;
                  padding: 1rem;
                  position: relative;
                  z-index: 10;
                }
                .header-content {
                  display: flex;
                  align-items: center;
                  position: relative;
                }
                .hamburger-button {
                  display: block;
                  background: none;
                  border: none;
                  padding: 0.5rem;
                  cursor: pointer;
                  position: absolute;
                  left: 0;
                  top: 50%;
                  transform: translateY(-50%);
                }
                .hamburger-icon,
                .hamburger-icon::before,
                .hamburger-icon::after {
                  display: block;
                  width: 24px;
                  height: 3px;
                  background: white;
                  border-radius: 2px;
                  transition: transform 0.3s ease, opacity 0.3s ease;
                }
                .hamburger-icon::before,
                .hamburger-icon::after {
                  content: '';
                  position: absolute;
                }
                .hamburger-icon::before {
                  top: -8px;
                }
                .hamburger-icon::after {
                  bottom: -8px;
                }
                .header-title-container {
                  flex-grow: 1;
                  text-align: center;
                  margin: 0 auto;
                }
                .header-title {
                  font-size: 1.5rem;
                  font-weight: bold;
                }
                .header-user {
                  font-size: 0.875rem;
                }
                .header-login-button {
                  background: #1a3c34;
                  color: white;
                  padding: 0.5rem 1rem;
                  border-radius: 0.25rem;
                  border: 1px solid white;
                }
                .header-login-button:hover {
                  background: #14532d;
                }
                .header-nav {
                  display: none;
                  flex-direction: column;
                  position: absolute;
                  top: 100%;
                  left: 0;
                  width: 200px;
                  background: #1a3c34;
                  padding: 1rem;
                  border-radius: 0 0 0.25rem 0.25rem;
                  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                  z-index: 999;
                }
                .header-nav-open {
                  display: flex;
                }
                .header-nav-button {
                  width: 100%;
                  padding: 0.5rem;
                  font-size: 0.9rem;
                  text-align: left;
                  margin-bottom: 0.5rem;
                  background: #6b7280;
                  color: white;
                  border-radius: 0.25rem;
                }
                .header-nav-button:hover {
                  background: #4b5563;
                }
                .header-logout-button {
                  background: #6b7280;
                  color: white;
                  padding: 0.5rem;
                  border-radius: 0.25rem;
                }
                .header-logout-button:hover {
                  background: #4b5563;
                }
                .header-logout-button.in-menu {
                  width: 100%;
                  text-align: left;
                  margin-bottom: 0.5rem;
                }
                .header-logout-button:not(.in-menu) {
                  position: absolute;
                  right: 1rem;
                  top: 50%;
                  transform: translateY(-50%);
                }
                .nav-buttons {
                  display: flex;
                  gap: 1rem;
                }
                .modal-overlay {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: rgba(0, 0, 0, 0.5);
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  z-index: 1000;
                }
                .modal-content {
                  background: white;
                  padding: 2rem;
                  border-radius: 0.5rem;
                  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
                  width: 90%;
                  max-width: 400px;
                  z-index: 1001;
                }
                .modal-title {
                  font-size: 1.5rem;
                  margin-bottom: 1rem;
                }
                .modal-error {
                  color: #ef4444;
                  margin-bottom: 1rem;
                }
                .modal-input {
                  width: 100%;
                  padding: 0.5rem;
                  margin-bottom: 1rem;
                  border: 1px solid #ddd;
                  border-radius: 0.25rem;
                  font-size: 1rem;
                  box-sizing: border-box;
                }
                .modal-textarea {
                  width: 100%;
                  padding: 0.5rem;
                  margin-bottom: 1rem;
                  border: 1px solid #ddd;
                  border-radius: 0.25rem;
                  font-size: 1rem;
                  height: 6rem;
                  resize: none;
                  box-sizing: border-box;
                }
                .modal-button {
                  padding: 0.5rem 1rem;
                  border-radius: 0.25rem;
                  border: none;
                  cursor: pointer;
                  font-size: 1rem;
                  margin: 0 0.25rem;
                }
                .modal-button-primary {
                  background: #007bff;
                  color: white;
                }
                .modal-button-primary:hover {
                  background: #0056b3;
                }
                .modal-button-secondary {
                  background: #6c757d;
                  color: white;
                }
                .modal-button-secondary:hover {
                  background: #5a6268;
                }
                .modal-button-danger {
                  background: #dc3545;
                  color: white;
                }
                .modal-button-danger:hover {
                  background: #c82333;
                }
                .modal-button-group {
                  display: flex;
                  justify-content: space-between;
                  gap: 0.5rem;
                }
                .main-page-container {
                  min-height: 100vh;
                  display: flex;
                  flex-direction: column;
                  position: relative;
                  z-index: 1;
                }
                .main-page-content {
                  padding: 2rem;
                  flex-grow: 1;
                  display: flex;
                  flex-direction: column;
                  position: relative;
                  z-index: 2;
                }
                .content-wrapper {
                  flex-grow: 1;
                  display: flex;
                  flex-direction: column;
                }
                .content-section {
                  background: white;
                  padding: 1rem;
                  border-radius: 0.5rem;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                  margin-bottom: 1rem;
                  position: relative;
                  z-index: 3;
                  width: 33.335%;
                  opacity: 0.5;
                }
                .content-title {
                  font-size: 1.25rem;
                  margin-bottom: 0.5rem;
                }
                .emergency-message {
                  background: rgba(255, 0, 0, 0.1);
                  padding: 1rem;
                  margin-bottom: 1rem;
                  border-radius: 0.5rem;
                  position: relative;
                  z-index: 3;
                  opacity: 1;
                }
                .emergency-title {
                  font-size: 1.25rem;
                  color: #991b1b;
                }
                .emergency-button {
                  background: #3b82f6;
                  color: white;
                  padding: 0.5rem 1rem;
                  border-radius: 0.25rem;
                }
                .emergency-button:hover {
                  background: #2563eb;
                }
                .weather-section {
                  background: white;
                  padding: 1rem;
                  border-radius: 0.5rem;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                  position: relative;
                  z-index: 3;
                  opacity: 0.5;
                }
                .weather-title {
                  font-size: 1.25rem;
                  margin-bottom: 0.5rem;
                  text-align: center;
                }
                .weather-grid {
                  display: grid;
                  grid-template-columns: repeat(3, 1fr);
                  gap: 1rem;
                  text-align: center;
                }
                .weather-chart {
                  margin-top: 1rem;
                  height: 12rem;
                }
                @media (max-width: 640px) {
                  .header-content {
                    flex-direction: row;
                    gap: 0;
                    align-items: center;
                    position: relative;
                  }
                  .header-title {
                    font-size: 1.25rem;
                  }
                  .header-user {
                    font-size: 0.75rem;
                  }
                  .header-login-button {
                    padding: 0.4rem 0.8rem;
                    font-size: 0.9rem;
                    width: 100%;
                    text-align: center;
                  }
                  .header-nav-button {
                    font-size: 0.9rem;
                  }
                  .header-logout-button {
                    font-size: 0.9rem;
                  }
                  .nav-buttons {
                    flex-direction: column;
                    width: 100%;
                  }
                  .modal-content-mobile {
                    width: 95%;
                    padding: 1.5rem;
                    max-width: 350px;
                  }
                  .modal-title {
                    font-size: 1.25rem;
                  }
                  .modal-error {
                    font-size: 0.875rem;
                  }
                  .modal-input,
                  .modal-textarea {
                    font-size: 0.9rem;
                    padding: 0.4rem;
                  }
                  .modal-button {
                    font-size: 0.9rem;
                    padding: 0.4rem 0.8rem;
                  }
                  .main-page-content {
                    padding: 1rem;
                  }
                  .content-section-mobile {
                    width: 100%;
                    padding: 0.75rem;
                  }
                  .content-title {
                    font-size: 1rem;
                  }
                  .emergency-message-mobile {
                    padding: 0.75rem;
                  }
                  .emergency-title {
                    font-size: 1rem;
                  }
                  .emergency-button {
                    padding: 0.4rem 0.8rem;
                    font-size: 0.9rem;
                  }
                  .weather-section-mobile {
                    padding: 0.75rem;
                  }
                  .weather-title {
                    font-size: 1rem;
                  }
                  .weather-grid-mobile {
                    grid-template-columns: 1fr;
                    gap: 0.5rem;
                  }
                  .weather-chart-mobile {
                    height: 10rem;
                  }
                }
              `}
            </style>
            <HeaderWrapper
              onLoginClick={handleLoginClick}
              isPublic={!user}
              user={user}
              isAdmin={isAdmin}
              onLogout={handleLogout}
              isMobile={isMobile}
            />
            <main className="flex-grow">
              <Routes>
                <Route
                  path="/"
                  element={
                    user ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <>
                        {showLogin && <LoginWindow onClose={handleLoginClose} isMobile={isMobile} />}
                        <MainPage
                          onLoginClick={handleLoginClick}
                          isPublic={true}
                          user={user}
                          isAdmin={isAdmin}
                          weatherData={weatherData}
                          historicalWeatherData={historicalWeatherData}
                          events={events}
                          eventsError={eventsError}
                          isMobile={isMobile}
                        />
                      </>
                    )
                  }
                />
                <Route
                  path="/rentals"
                  element={<RentalsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />}
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <MainPage
                        isPublic={false}
                        user={user}
                        isAdmin={isAdmin}
                        weatherData={weatherData}
                        historicalWeatherData={historicalWeatherData}
                        events={events}
                        eventsError={eventsError}
                        isMobile={isMobile}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/cameras"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <CamerasPage isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/community-pics"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <CommunityPicsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/community-pics/:id"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <MediaDetailPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/invoices"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <InvoicesPage isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/owners"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <OwnersPage isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/contractors"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <ContractorsPage isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <CalendarPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/board-members"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <BoardMembersPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/meeting-minutes"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <MeetingMinutesPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hoa-docs"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <HOADocsPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin-invoices"
                  element={
                    <ProtectedRoute isMobile={isMobile}>
                      <AdminInvoicesPage user={user} isAdmin={isAdmin} isMobile={isMobile} />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </main>
          </div>
        </Router>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
export { db };